import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  assignSubject,
  createAssignment,
  createQuestionClass,
  createQuestionProject,
  createQuestionSubject,
  deleteQuestionClass,
  deleteQuestionProject,
  deleteQuestionSubject,
  questionQueryKeys,
  removeAssignment,
  resetPapers,
  unassignSubject,
  updateAssignedSubjects,
  updateQuestionClass,
  updateQuestionProject,
  updateQuestionSubject,
  uploadPaper,
} from '../api/question.api';
import type {
  AssignedSubjectPayload,
  CreateAssignmentPayload,
  QuestionClassPayload,
  QuestionProjectPayload,
  QuestionSubjectPayload,
} from '../types/question.types';

interface SaveArgs<TPayload> {
  id: number | null;
  payload: TPayload;
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: questionQueryKeys.all() });
  };
}

export function useSaveQuestionClass(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<QuestionClassPayload>) =>
      id === null ? createQuestionClass(payload) : updateQuestionClass(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Class created' : 'Class updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the class'),
  });
}

export function useDeleteQuestionClass(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: number) => deleteQuestionClass(id),
    onSuccess: () => {
      invalidate();
      toast.success('Class removed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to remove the class'),
  });
}

export function useSaveQuestionSubject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<QuestionSubjectPayload>) =>
      id === null ? createQuestionSubject(payload) : updateQuestionSubject(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Subject created' : 'Subject updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the subject'),
  });
}

export function useDeleteQuestionSubject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: number) => deleteQuestionSubject(id),
    onSuccess: () => {
      invalidate();
      toast.success('Subject removed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to remove the subject'),
  });
}

export function useAssignSubject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ classId, subjectId }: { classId: number; subjectId: number }) =>
      assignSubject(classId, subjectId),
    onSuccess: () => {
      invalidate();
      toast.success('Subject put on the class');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to assign the subject'),
  });
}

export function useUnassignSubject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (classSubjectId: number) => unassignSubject(classSubjectId),
    onSuccess: () => {
      invalidate();
      toast.success('Subject taken off the class');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to remove the subject'),
  });
}

export function useSaveQuestionProject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<QuestionProjectPayload>) =>
      id === null ? createQuestionProject(payload) : updateQuestionProject(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Project created' : 'Project updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the project'),
  });
}

export function useDeleteQuestionProject(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: number) => deleteQuestionProject(id),
    onSuccess: () => {
      invalidate();
      toast.success('Project deleted');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to delete the project'),
  });
}

export function useCreateAssignment(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ projectId, payload }: { projectId: number; payload: CreateAssignmentPayload }) =>
      createAssignment(projectId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('Institute assigned');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to assign the institute'),
  });
}

export function useUpdateAssignedSubjects(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      projectInstituteId,
      subjects,
    }: {
      projectInstituteId: number;
      subjects: AssignedSubjectPayload[];
    }) => updateAssignedSubjects(projectInstituteId, subjects),
    onSuccess: () => {
      invalidate();
      toast.success('Subjects updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to update the subjects'),
  });
}

export function useRemoveAssignment(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (projectInstituteId: number) => removeAssignment(projectInstituteId),
    onSuccess: () => {
      invalidate();
      toast.success('Institute taken off');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to take the institute off'),
  });
}

export function useUploadPaper(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ instituteSubjectId, file }: { instituteSubjectId: number; file: File }) =>
      uploadPaper(instituteSubjectId, file),
    onSuccess: () => {
      invalidate();
      toast.success('Paper uploaded');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to upload the paper'),
  });
}

export function useResetPapers(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (instituteSubjectId: number) => resetPapers(instituteSubjectId),
    onSuccess: () => {
      invalidate();
      toast.success('Subject reset - every file and its view history is gone');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to reset the subject'),
  });
}
